import React, {
	ChangeEvent,
	forwardRef,
	KeyboardEvent,
	Ref,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	Box,
	Button,
	ButtonBase,
	Card,
	CardContent,
	CircularProgress,
	FormControl,
	Grid2,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useMainCustomStyle } from '../../../assets/theme/Theme';
import {
	BackArrowIcon,
	ChatBotBubbleIcon,
	ChatBotIcon,
	ChatConsultant,
	DeleteIcon,
	DoubleTick,
	DownloadIcon,
	InfoIcon,
	SendChatIcon,
	SendLinkIcon,
	SingleTick,
} from '../../../assets/icons/Icons';
import {
	assignCustomerToClient,
	downloadMediaFile,
	fetchTestChats,
	fetchWhatsappUsers,
	getCustomerChat,
	onFetchKnowledgeUploads,
	onFetchKnowledgeUploadUrls,
	onRemoveKnowledgeUpload,
	onRemoveKnowledgeUploadUrl,
	sendStudioChatMessage,
	uploadKnowledgePdf,
	uploadSelectedWebUrl,
	uploadWebUrl,
} from '../../../services/ApiService';
import ChatLoader from '../../../assets/images/ChatLoader.gif';
import { MAIN_THEME_COLOR, WHITE } from '../../../assets/theme/Colors';
import { v4 as uuidV4 } from 'uuid';
import {
	getAccessToken,
	getAgencyId,
	getFeatureLimit,
	getFeatureLimitStatus,
	getUserEmail,
	getUserId,
	getUserSessionId,
	getUserType,
	setAllFeatureLimitStatus,
	setFeatureLimit,
	setFeatureLimitStatus,
	setUserSessionId,
	setUserType,
} from '../../../Utils/AuthUtil';
import {
	fetchAllAdmins,
	fetchGuestUserLimits,
	onAddClickStat,
	updateGuestUserLimits,
} from '../../../services/UserService';
import { convertTo12HourTime, dateFormatter } from '../../../Utils/Helpers';
import _ from 'lodash';
import SuperAdminSideNavigationBar from '../../../components/SuperAdminSideNavigationBar';
import ChatNumbersList from './ChatNumbersList';
import {
	Admin,
	ChatsResponse,
	SuperAdminChatList,
	TestChatResponse,
} from '../../../types';
import Avatar from '../../../assets/images/Avatar.png';
import { toast } from 'react-toastify';
import VoiceMessagePlayer from '../../../components/VoiceMessagePlayer';

const defaultOrgId = process.env.REACT_APP_DEFAULT_ORG_ID || '';

interface Chat {
	text: string;
	isReceived?: boolean;
	position: string;
	time: string;
	fileName?: string;
	fileUrl?: string;
	caption?: string;
	chatOwner: string;
	status: string | null;
	isVoice?: boolean;
}

interface SelectedChat {
	phoneNumber: string;
	name: string;
	isConsultantNeeded: boolean;
	phoneNumberExtra: string;
	orgId: string;
	packageName: string | null;
}

interface ChatNumberResponse {
	success: boolean;
	count: number;
	whatsapp_users: Array<{
		user_id: string;
		user_name: string;
		org_id: string;
		consultant_mode: boolean;
		shared_pkg_org: string | null;
		phone_number: string;
	}>;
}

interface ChatRowProps {
	chat: Chat;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
	voiceUrl: string | undefined;
}

interface ChatBubbleProps {
	data: Chat;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
	voiceUrl: string | undefined;
}

interface ChatBodyWindowProps {
	isChatView: boolean;
	chatData: { [index: string]: Array<Chat> };
	loading: boolean;
	onDownloadFile: (url: string) => void;
	downloadingFile: string;
	isDownloading: boolean;
	selectedChat: SelectedChat;
	isMobile: boolean;
	onclickGoBack: () => void;
	isClientAssigned: boolean;
	clients: Array<Admin>;
	handleChangeClient: (event: SelectChangeEvent) => void;
	selectedClient: string;
	onAssignClient: () => void;
	selectedClientName: string;
	onShowAssignmentCard: boolean;
	packageName: string | null;
	audioUrls: Record<string, string>;
}

interface AssignmentCardProps {
	isClientAssigned: boolean;
	clients: Array<Admin>;
	handleChangeClient: (event: SelectChangeEvent) => void;
	selectedClient: string;
	onAssignClient: () => void;
	selectedClientName: string;
	packageName: string | null;
}

function ChatBubble({
	data,
	onDownloadFile,
	isDownloading,
	downloadingFile,
	voiceUrl,
}: ChatBubbleProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		} else {
			return {
				borderTopLeftRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		if (textLength > 40) {
			return true;
		} else {
			return false;
		}
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	function getChatIcon(type: string) {
		if (type === 'consultant') {
			return <ChatConsultant />;
		} else if (type === 'ai') {
			return <ChatBotBubbleIcon />;
		} else {
			return <></>;
		}
	}

	function getChatStatusIcon(type: string | null) {
		if (type === 'delivered') {
			return <DoubleTick color="#aaaaaa" />;
		} else if (type === 'seen') {
			return <DoubleTick color="#34B7F1" />;
		} else {
			return <SingleTick color="#aaaaaa" />;
		}
	}

	return (
		<>
			<Grid2
				component={'section'}
				sx={{
					paddingX: '20px',
					paddingY: '10px',
					marginX: '5px',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor:
						data.position === 'left' ? '#F2F4F5' : MAIN_THEME_COLOR,
					marginTop: data.position === 'right' ? '15px' : '0px',
					width: !isMobile
						? isMessageIsTooLong()
							? '40%'
							: null
						: null,
					...getChatBubbleStyle(),
					minWidth: 0,
				}}>
				{data.position === 'right' && (
					<Typography
						variant="body2"
						textAlign={'left'}
						color={'#E2FAFB'}
						fontWeight={'700'}
						paddingY={'5px'}>
						{getChatOwnerTag(data.chatOwner)}
					</Typography>
				)}
				{data.isVoice ? (
					<VoiceMessagePlayer
						url={voiceUrl ?? ''}
						isSender={data.position === 'right'}
					/>
				) : data.fileName && data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						<Grid2
							container
							flexDirection={'row'}
							width={'100%'}
							alignItems={'center'}
							sx={{
								backgroundColor:
									data.position === 'left'
										? '#F0F0F0'
										: MAIN_THEME_COLOR,
								padding: '5px',
								borderRadius: '8px',
								boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
							}}>
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									maxWidth: isMobile
										? 'calc(90% - 10px)'
										: 'calc(98% - 10px)',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{data.fileName}
							</Typography>
							{isDownloading &&
							downloadingFile === data.fileUrl ? (
								<CircularProgress
									sx={{
										color:
											data.position === 'left'
												? '#28696D'
												: '#FFFFFF',
										marginLeft: '5px',
										padding: '2px',
									}}
									size={24}
								/>
							) : (
								<IconButton
									sx={{
										marginLeft: '5px',
										padding: '2px',
									}}
									onClick={() =>
										onDownloadFile &&
										onDownloadFile(
											data.fileUrl ? data.fileUrl : '',
										)
									}>
									<DownloadIcon
										color={
											data.position === 'left'
												? '#000000'
												: '#FFFFFF'
										}
									/>
								</IconButton>
							)}
						</Grid2>
						<Typography
							variant="caption"
							sx={{
								color:
									data.position === 'left'
										? '#8E8E8E'
										: '#B0B0B0',
								maxWidth: '100%',
								overflowWrap: 'break-word',
								paddingTop: '5px',
								paddingLeft: '5px',
							}}>
							{data.caption}
						</Typography>
					</Grid2>
				) : (
					<Typography
						variant="body2"
						style={{
							overflowWrap: 'break-word',
							whiteSpace: 'pre-line',
						}}
						color={
							data.position === 'left' ? '#000000' : '#FFFFFF'
						}>
						{data.text}
					</Typography>
				)}
				<Typography
					fontSize={'8px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}
					paddingTop={'5px'}
					alignItems={'center'}>
					{data.time}
					&nbsp;&nbsp;
					{data.position === 'right' &&
						getChatStatusIcon(data.status)}
				</Typography>
			</Grid2>
			{data.position === 'right' && getChatIcon(data.chatOwner)}
		</>
	);
}

function ChatRow({
	chat,
	onDownloadFile,
	isDownloading,
	downloadingFile,
	voiceUrl,
}: ChatRowProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}>
			<ChatBubble
				data={chat}
				onDownloadFile={onDownloadFile}
				downloadingFile={downloadingFile}
				isDownloading={isDownloading}
				voiceUrl={voiceUrl}
			/>
		</Grid2>
	);
}
const ChatBodyWindow = forwardRef(function ChatBodyWindow(
	{
		isChatView,
		chatData,
		loading,
		onDownloadFile,
		downloadingFile,
		isDownloading,
		clients,
		isClientAssigned,
		handleChangeClient,
		selectedClient,
		onAssignClient,
		selectedClientName,
		onShowAssignmentCard,
		packageName,
		audioUrls,
	}: ChatBodyWindowProps,
	ref: Ref<HTMLDivElement>,
) {
	if (isChatView && !loading) {
		return (
			<Box
				ref={ref}
				sx={{
					overflowY: 'auto',
					height: '80vh',
					width: '100%',
					'&::-webkit-scrollbar': {
						display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
					},
					'&-ms-overflow-style:': {
						display: 'none', // Hide the scrollbar for IE
					},
				}}>
				<Grid2 padding={'10px'}>
					{Object.keys(chatData).map((item, index) => {
						return (
							<Grid2 key={index}>
								<Typography
									variant="body2"
									fontWeight={'500'}
									textAlign={'center'}>
									{dateFormatter(item)}
								</Typography>
								{chatData[item].map((chat, i) => {
									const voiceUrl = chat.fileUrl
										? audioUrls[chat.fileUrl.split('+')[1]]
										: undefined;

									return (
										<ChatRow
											key={i}
											chat={chat}
											onDownloadFile={onDownloadFile}
											downloadingFile={downloadingFile}
											isDownloading={isDownloading}
											voiceUrl={voiceUrl}
										/>
									);
								})}
							</Grid2>
						);
					})}
					{onShowAssignmentCard && (
						<Grid2
							container
							justifyContent={'center'}
							marginTop={'10px'}>
							<AssignmentCard
								isClientAssigned={isClientAssigned}
								clients={clients}
								handleChangeClient={handleChangeClient}
								selectedClient={selectedClient}
								onAssignClient={onAssignClient}
								selectedClientName={selectedClientName}
								packageName={packageName}
							/>
						</Grid2>
					)}
				</Grid2>
			</Box>
		);
	} else if (isChatView && loading) {
		return (
			<Grid2
				container
				width={'100%'}
				height={'70vh'}
				justifyContent={'center'}
				alignItems={'center'}>
				<CircularProgress sx={{ color: MAIN_THEME_COLOR }} />
			</Grid2>
		);
	} else {
		return (
			<Grid2
				container
				width={'100%'}
				height={'70vh'}
				justifyContent={'center'}
				alignItems={'center'}>
				<Typography fontSize={'16px'} fontWeight={'400'}>
					No chat data available
				</Typography>
			</Grid2>
		);
	}
});

function ChatWindowMain(
	{
		isChatView,
		chatData,
		loading,
		onDownloadFile,
		downloadingFile,
		isDownloading,
		selectedChat,
		isMobile,
		onclickGoBack,
		isClientAssigned,
		clients,
		handleChangeClient,
		selectedClient,
		onAssignClient,
		selectedClientName,
		onShowAssignmentCard,
		packageName,
		audioUrls,
	}: ChatBodyWindowProps,
	messagesEndRef: Ref<HTMLDivElement>,
) {
	function isToPhoneNumberTooLong(number: string) {
		if (selectedChat.name != 'Unknown') {
			if (isMobile && number.length > 21) {
				return number.slice(0, 21) + '...';
			} else {
				return number;
			}
		}

		return '';
	}

	return (
		<Grid2 component="div" size={{ xs: 12, md: 8 }}>
			<Grid2
				bgcolor={'#FFFFFF'}
				borderRadius={'5px'}
				sx={{ height: '100%' }}>
				<Grid2
					container
					justifyContent={'space-between'}
					alignItems={'center'}
					padding={'10px'}>
					{selectedChat.phoneNumber !== '' && (
						<Grid2 container alignItems={'center'}>
							{isMobile && (
								<IconButton onClick={onclickGoBack}>
									<BackArrowIcon />
								</IconButton>
							)}
							<Box
								component={'img'}
								src={Avatar}
								width={40}
								height={40}
							/>
							<Grid2
								component={'div'}
								flexDirection={'row'}
								paddingLeft="15px">
								<Typography
									fontWeight={'500'}
									color="#000000"
									fontSize={'14px'}>
									{selectedChat.name != 'Unknown'
										? selectedChat.name
										: selectedChat.phoneNumber}
								</Typography>
								<Typography
									fontWeight={'500'}
									color="#7c7c7c"
									fontSize={'12px'}>
									{isToPhoneNumberTooLong(
										selectedChat.phoneNumberExtra,
									)}
								</Typography>
							</Grid2>
						</Grid2>
					)}
				</Grid2>
				<Grid2 container>
					<ChatBodyWindow
						isChatView={isChatView}
						chatData={chatData}
						ref={messagesEndRef}
						loading={loading}
						isDownloading={isDownloading}
						downloadingFile={downloadingFile}
						onDownloadFile={onDownloadFile}
						selectedChat={selectedChat}
						isMobile={isMobile}
						onclickGoBack={onclickGoBack}
						isClientAssigned={selectedChat.orgId !== defaultOrgId}
						clients={clients}
						handleChangeClient={handleChangeClient}
						selectedClient={selectedClient}
						onAssignClient={onAssignClient}
						selectedClientName={selectedClientName}
						onShowAssignmentCard={onShowAssignmentCard}
						packageName={packageName}
						audioUrls={audioUrls}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

const ForwardedChatWindowMain = forwardRef(ChatWindowMain);

function AssignmentCard({
	isClientAssigned,
	clients,
	handleChangeClient,
	selectedClient,
	onAssignClient,
	selectedClientName,
	packageName,
}: AssignmentCardProps) {
	if (isClientAssigned) {
		return (
			<Paper
				elevation={0}
				sx={{
					bgcolor: `${MAIN_THEME_COLOR}20`,
					border: '1px solid',
					borderColor: MAIN_THEME_COLOR,
					borderRadius: 2,
					p: 2,
					maxWidth: 400,
					mx: 'auto',
				}}>
				<Grid2 container spacing={1}>
					<Grid2 container alignItems="center" spacing={1}>
						<Grid2 size={{ xs: 12 }}>
							<Typography
								variant="subtitle2"
								color={MAIN_THEME_COLOR}
								fontWeight={600}>
								System Notification
							</Typography>
						</Grid2>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="body2" color="primary.800">
							Assigned to {selectedClientName}
						</Typography>
						{packageName && (
							<Typography
								sx={{
									color: 'gray.600',
									fontSize: '12px',
									marginY: '5px',
								}}>
								Assigned Package: {packageName}
							</Typography>
						)}
						<Typography
							sx={{ color: MAIN_THEME_COLOR, fontSize: '10px' }}>
							Assigned by: Super Admin
						</Typography>
					</Grid2>
				</Grid2>
			</Paper>
		);
	} else {
		return (
			<Card
				sx={{
					maxWidth: 500,
					width: '100%',
					border: '2px solid',
					borderColor: 'grey.200',
					borderRadius: 3,
					boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
					background:
						'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
				}}>
				<CardContent sx={{ p: 3 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							mb: 2,
						}}>
						<Typography
							sx={{
								fontWeight: 600,
								color: MAIN_THEME_COLOR,
								fontSize: '16px',
							}}>
							Assignment Required
						</Typography>
					</Box>
					<Typography
						fontSize={'14px'}
						color="grey.600"
						sx={{ mb: 1 }}>
						Select a travel company to assign this traveler to based
						on their preferences and requirements.
					</Typography>
					{packageName && (
						<Grid2
							container
							alignItems={'center'}
							paddingBottom={'10px'}>
							<Typography
								fontSize={'12px'}
								color="black.600"
								marginRight={'5px'}>
								Package:
							</Typography>
							<Box
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									px: 1.5,
									py: 0.5,
									borderRadius: '10px',
									fontWeight: 500,
									backgroundColor: `${MAIN_THEME_COLOR}20`,
									color: MAIN_THEME_COLOR,
								}}>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										color: 'inherit',
									}}>
									{packageName}
								</Typography>
							</Box>
						</Grid2>
					)}
					<Box sx={{ spaceY: 4 }}>
						<FormControl fullWidth size="medium" sx={{ mb: 0 }}>
							<Select
								label="Select a travel company..."
								displayEmpty
								disableUnderline
								variant="filled"
								value={selectedClient}
								renderValue={selected => {
									if (!selected) {
										return (
											<Typography
												sx={{
													opacity: 0.7,
													fontStyle: 'italic',
													fontSize: '12px',
												}}>
												Select a travel company...
											</Typography>
										);
									}
									return (
										clients.find(
											client =>
												client.agencyId === selected,
										)?.username || selected
									);
								}}
								onChange={handleChangeClient}
								sx={{
									border: '1px solid',
									borderColor: 'grey.300',
									borderRadius: 2,
									'&:hover': { borderColor: 'blue.400' },
									transition: 'border-color 0.2s',
									'& .MuiFilledInput-input': {
										padding: '10.5px 10px', // Match default filled input padding
									},
									fontSize: '14px',
								}}>
								{clients.map(client => (
									<MenuItem
										key={client.id}
										value={client.agencyId}>
										{client.username}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
							<Button
								fullWidth
								variant="contained"
								disabled={selectedClient !== '' ? false : true}
								size="medium"
								sx={{
									py: 1,
									borderRadius: 2,
									textTransform: 'none',
									fontWeight: 500,
									backgroundColor: MAIN_THEME_COLOR,
									'&:hover': {
										backgroundColor: MAIN_THEME_COLOR,
									},
									'&.Mui-disabled': {
										backgroundColor: 'grey.300',
									},
								}}
								onClick={onAssignClient}>
								Assign Company
							</Button>
						</Box>
					</Box>
				</CardContent>
			</Card>
		);
	}
}

function SuperAdminConversations() {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const userId = getUserId();
	const orgId = getAgencyId();
	const theme = useTheme();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [chatId, setChatId] = useState<string | null>(null);
	const [isChatWindow, setIsChatWindow] = useState(false);
	const [chatData, setChatData] = useState<{
		[index: string]: Array<Chat>;
	}>({});
	const [loading, setLoading] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadingFile, setDownloadingFile] = useState('');
	const [isClientAssigned, setIsClientAssigned] = useState(false);
	const [chatListData, setChatListData] = useState<Array<SuperAdminChatList>>(
		[],
	);
	const [chatListAllData, setChatListAllData] = useState<
		Array<SuperAdminChatList>
	>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedChat, setSelectedChat] = useState<SelectedChat>({
		phoneNumber: '',
		name: '',
		isConsultantNeeded: false,
		phoneNumberExtra: '',
		orgId: '',
		packageName: null,
	});
	const [showNumberList, setShowNumberList] = useState(true);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [clients, setClients] = useState<Array<Admin>>([]);
	const [selectedClient, setSelectedClient] = useState('');
	const [selectedClientName, setSelectedClientName] = useState('');
	const [onShowAssignmentCard, setOnShowAssignmentCard] = useState(false);
	const [assignedPackageName, setAssignedPackageName] = useState<
		string | null
	>(null);
	const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});

	useLayoutEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.style.scrollBehavior = 'auto';
			messagesEndRef.current.scrollTop =
				messagesEndRef.current.scrollHeight;

			requestAnimationFrame(() => {
				if (messagesEndRef.current) {
					messagesEndRef.current.style.scrollBehavior = 'smooth';
				}
			});
		}
	}, [loading, chatData]);

	useEffect(() => {
		fetchChatNumbersList();
		getAllClients();
	}, []);

	async function getAllClients() {
		const data = await fetchAllAdmins(token ? token : '');
		setClients(data.content.reverse());
	}

	async function fetchChatNumbersList(enableLoading = true) {
		if (enableLoading) {
			setLoading(true);
		}
		const data: ChatNumberResponse = await fetchWhatsappUsers();

		const chatList: Array<SuperAdminChatList> = data.whatsapp_users.map(
			item => {
				return {
					name: item.user_name ? item.user_name : item.user_id,
					phoneNumber: item.user_id,
					status:
						item.org_id === defaultOrgId
							? item.consultant_mode
								? 'Escalated'
								: 'Pending'
							: 'Assigned',
					orgId: item.org_id,
					isConsultantNeeded: item.consultant_mode,
					packageName: item.shared_pkg_org,
					phoneNumberExtra: item.phone_number,
				};
			},
		);

		setChatListData(chatList);
		setChatListAllData(chatList);
		setLoading(false);
	}

	async function onFetchChats(chat: SelectedChat) {
		setLoading(true);
		setSelectedClient('');
		const data: TestChatResponse = await getCustomerChat(chat.phoneNumber);

		if (data.conversation.length === 0) {
			setLoading(false);
			return;
		}

		setOnShowAssignmentCard(chat.isConsultantNeeded);
		setAssignedPackageName(chat.packageName);
		setIsClientAssigned(chat.orgId !== defaultOrgId); //check if client is assigned here
		setChatId(data.user_id);
		setUserSessionId(data.user_id);
		setSelectedChat(chat);
		if (chat.orgId !== defaultOrgId) {
			const client = clients.find(
				client => client.agencyId === chat.orgId,
			);
			setSelectedClientName(client?.username || 'Unknown');
		}

		const updatedData: Array<Chat> = data.conversation.map(item => {
			const type = item.type;
			const msg = item.content;
			const fileData = item.file ? JSON.parse(msg) : null;
			// Parse WhatsApp human data (if present)
			const humanData =
				typeof msg === 'string' &&
				msg.includes('"MIME_type"') &&
				msg.includes('"ID"')
					? JSON.parse(msg)
					: null;
			const mimeType = fileData?.type || humanData?.MIME_type;
			const mediaId = fileData?.media_id || humanData?.ID;
			const position = item.type === 'human' ? 'left' : 'right';
			const isVoice = mimeType?.startsWith('audio');

			const itemFileName =
				fileData &&
				(Object.prototype.hasOwnProperty.call(fileData, 'file_name') ||
					Object.prototype.hasOwnProperty.call(fileData, 'filename'))
					? fileData.filename || fileData.file_name
					: humanData?.File_name ||
						(mimeType?.startsWith('image')
							? 'Image File'
							: mimeType?.startsWith('audio')
								? 'Voice Message'
								: 'Pdf File');

			return {
				text: msg,
				position: position,
				isReceived: true,
				time: convertTo12HourTime(item.time_stamp.split(' ')[1]),
				chatOwner: type,
				date: item.time_stamp.split(' ')[0],
				fileName: itemFileName,
				fileUrl:
					item.file && mediaId && mimeType
						? `${itemFileName}+${mediaId}+${mimeType}`
						: undefined,
				caption: item.file ? fileData.caption : '',
				status: item.seen_status,
				isVoice,
			};
		});

		const groupByDate = _.groupBy(updatedData, 'date');

		// Order Object By Date
		const entries = _.toPairs(groupByDate);
		const sortedEntries = _.orderBy(
			entries,
			[entry => new Date(entry[0])],
			['asc'],
		);
		const sortedObj = _.fromPairs(sortedEntries);
		Object.values(sortedObj)
			.flat()
			.forEach(message => {
				const mediaId = message.fileUrl?.split('+')[1];

				if (
					message.fileUrl?.includes('audio') &&
					mediaId &&
					!audioUrls[mediaId]
				) {
					getAudioUrl(mediaId).then(url => {
						setAudioUrls(prev => ({
							...prev,
							[mediaId]: url,
						}));
					});
				}
			});

		setChatData(sortedObj);
		setLoading(false);
		setIsChatWindow(true);
	}

	async function getAudioUrl(mediaId: string) {
		const response = await downloadMediaFile(
			mediaId,
			userId ?? '',
			orgId ?? '',
		);

		if (!response.error) {
			return URL.createObjectURL(response.data);
		}

		return '';
	}

	async function onDownloadFileWithUrl(url: string) {
		setIsDownloading(true);
		setDownloadingFile(url);
		const downloadData = url.split('+');
		const response = await downloadMediaFile(
			downloadData[1],
			userId ? userId : '',
			orgId ? orgId : '',
		);

		if (response.error) {
			console.error(
				'Error downloading the media file:',
				response.message,
			);
			return;
		}

		const blobData = response.data;
		const contentType =
			downloadData[2] !== '' ? downloadData[2] : 'application/pdf';
		const blob = new Blob([blobData], { type: contentType });
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = downloadData[0];
		downloadLink.click();
		URL.revokeObjectURL(downloadLink.href);
		setIsDownloading(false);
		setDownloadingFile('');
	}

	function onSearchChatList(search: string): void {
		setSearchTerm(search);
		const numberRegex = /\d/;
		const searchTag = numberRegex.test(search) ? 'phoneNumber' : 'name';

		if (search === '') {
			setChatListData(chatListAllData);
		} else {
			const searchResults: Array<SuperAdminChatList> =
				chatListAllData.filter(value => {
					return value[searchTag]
						.toLowerCase()
						.match(search.toLowerCase());
				});

			if (searchResults.length > 0) {
				setChatListData(searchResults);
			} else {
				setChatListData([]);
			}
		}
	}

	function onChangeGridVisibility() {
		setShowNumberList(!showNumberList);
	}

	function handleChangeClient(event: SelectChangeEvent) {
		setSelectedClient(event.target.value);
	}

	async function onAssignClient() {
		const response = await assignCustomerToClient(
			selectedChat.phoneNumber,
			selectedClient,
		);

		if (response.success) {
			setSelectedChat(prevState => ({
				...prevState,
				orgId: selectedClient,
			}));

			const name = clients.find(
				client => client.agencyId === selectedClient,
			)?.username;
			setSelectedClientName(name || 'Unknown');
			const updatedChatListData = chatListData.map(item => {
				return item.phoneNumber === selectedChat.phoneNumber
					? { ...item, status: 'Assigned' }
					: item;
			});
			setChatListData(updatedChatListData);

			toast.success(`Customer assigned Successfully`);
		} else {
			toast.error(response.message);
		}
	}

	function renderBodyContent() {
		if (isMobile) {
			if (showNumberList) {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onFetchChats}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							searchedText={searchTerm}
							isMobile={isMobile}
						/>
					</Grid2>
				);
			} else {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ForwardedChatWindowMain
							isChatView={isChatWindow}
							chatData={chatData}
							ref={messagesEndRef}
							loading={loading}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							onDownloadFile={onDownloadFileWithUrl}
							selectedChat={selectedChat}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							clients={clients}
							isClientAssigned={isClientAssigned}
							handleChangeClient={handleChangeClient}
							selectedClient={selectedClient}
							onAssignClient={onAssignClient}
							selectedClientName={selectedClientName}
							onShowAssignmentCard={onShowAssignmentCard}
							packageName={assignedPackageName}
							audioUrls={audioUrls}
						/>
					</Grid2>
				);
			}
		} else {
			return (
				<Grid2
					container
					paddingLeft={'20px'}
					paddingRight={'20px'}
					flexDirection={'column'}>
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={1}
						height={'85vh'}
						rowSpacing={{ xs: 1 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onFetchChats}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							searchedText={searchTerm}
							isMobile={isMobile}
						/>
						<ForwardedChatWindowMain
							isChatView={isChatWindow}
							chatData={chatData}
							ref={messagesEndRef}
							loading={loading}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							onDownloadFile={onDownloadFileWithUrl}
							selectedChat={selectedChat}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							clients={clients}
							isClientAssigned={isClientAssigned}
							handleChangeClient={handleChangeClient}
							selectedClient={selectedClient}
							selectedClientName={selectedClientName}
							onAssignClient={onAssignClient}
							onShowAssignmentCard={onShowAssignmentCard}
							packageName={assignedPackageName}
							audioUrls={audioUrls}
						/>
					</Grid2>
				</Grid2>
			);
		}
	}

	return (
		<>
			<SuperAdminSideNavigationBar />
			<div style={mainDivStyle}>{renderBodyContent()}</div>
		</>
	);
}

export default SuperAdminConversations;
